// ────────────────────────────────────────────────────────────────────────────
// Hand off from Nexus Dex into Pokémon GO.
//
// iOS publishes a URL scheme (`pokemongo://`) that any app may open, and the
// LSApplicationQueriesSchemes entry in Info.plist lists it. Android launches the
// same scheme, falling back to the package intent.
//
// WHY THIS IS A PLAIN NAVIGATION AND NOT A PLUGIN CALL. It used to go through
// `App.openUrl` / `App.canOpenUrl` from @capacitor/app — neither of which
// exists. The plugin's API is exitApp / getInfo / getState / getLaunchUrl /
// minimizeApp / getAppLanguage / listeners, and has been for several major
// versions, so the guard `typeof App.openUrl !== "function"` was true every
// time and this function returned `plugin_unavailable` and did nothing, on every
// platform, for as long as it has existed. Setting `location.href` to a custom
// scheme is what actually hands off from a WKWebView, and is what the signup
// panel has been doing correctly all along.
//
// HOW WE KNOW IT WORKED. There is no callback for "another app took over", so
// we watch for this app going to the background. The signal that matters is
// Capacitor's `appStateChange`, because `visibilitychange` is NOT reliable in a
// WKWebView — it frequently does not fire when iOS backgrounds the app, which is
// exactly how the first version of this ended up telling trainers "could not
// open Pokémon GO, is it installed?" immediately after successfully opening
// Pokémon GO. The web events are kept as a fallback for the browser build only.
//
// The window is generous, and the failure is reported conservatively: a wrong
// "it did not work" on a switch that plainly worked is far more damaging than
// staying quiet about a rare genuine failure.
// ────────────────────────────────────────────────────────────────────────────
import { getPlatform } from "./platform";
import { openPokemonGoNative } from "./broadcastAutoStart";

export interface OpenPokemonGoResult {
  handed_off: boolean;
  reason?: "platform" | "scheme_unavailable" | "open_failed";
  error?: string;
}

const POKEMON_GO_SCHEME = "pokemongo://";
const ANDROID_INTENT =
  "intent://#Intent;scheme=pokemongo;package=com.nianticlabs.pokemongo;end";

/**
 * How long to wait for the OS to switch away before calling it a failure.
 *
 * A cold Pokémon GO launch is not instant, and the app-state callback arrives
 * only once iOS has actually moved us to the background. 1.2s was short enough
 * to lose that race on a real device.
 */
const HANDOFF_GRACE_MS = 3000;

export async function openPokemonGo(): Promise<OpenPokemonGoResult> {
  const platform = getPlatform();
  if (platform !== "ios" && platform !== "android") {
    return { handed_off: false, reason: "platform" };
  }
  if (typeof window === "undefined" || typeof document === "undefined") {
    return { handed_off: false, reason: "platform" };
  }

  // Android (2026-09-23): the launch intent through the native plugin. The
  // `intent://` navigation below is kept only as the fallback for a build
  // without the method: Capacitor wraps non-app URLs in a plain ACTION_VIEW,
  // which nothing claims for the intent scheme, so it never opened GO.
  if (platform === "android") {
    const native = await openPokemonGoNative();
    if (native?.opened) return { handed_off: true };
    if (native && native.reason === "not_installed") {
      return { handed_off: false, reason: "scheme_unavailable" };
    }
  }

  const departed = watchForDeparture();
  try {
    window.location.href = platform === "android" ? ANDROID_INTENT : POKEMON_GO_SCHEME;
  } catch (err) {
    departed.cancel();
    return {
      handed_off: false,
      reason: "open_failed",
      error: err instanceof Error ? err.message : String(err),
    };
  }

  const left = await departed.result;
  return left ? { handed_off: true } : { handed_off: false, reason: "scheme_unavailable" };
}

/**
 * Resolve true when this app goes to the background within the grace window —
 * which is what "another app came to the front" looks like from in here.
 *
 * Capacitor's appStateChange is the authoritative signal on device. The DOM
 * events are a fallback for the web build, where there is no plugin, and are
 * deliberately not trusted alone on native.
 */
function watchForDeparture(): { result: Promise<boolean>; cancel: () => void } {
  let settle: (v: boolean) => void = () => {};
  let timer: ReturnType<typeof setTimeout> | null = null;
  let removeNative: (() => void) | null = null;
  let settled = false;

  const onHide = () => {
    if (document.visibilityState === "hidden") finish(true);
  };
  const onPageHide = () => finish(true);

  function finish(value: boolean) {
    if (settled) return;
    settled = true;
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("pagehide", onPageHide);
    removeNative?.();
    settle(value);
  }

  const result = new Promise<boolean>((resolve) => {
    settle = resolve;
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onPageHide);
    timer = setTimeout(() => finish(false), HANDOFF_GRACE_MS);
  });

  // Native signal, attached asynchronously. The listener is registered after the
  // navigation has been kicked off, which is fine: iOS takes longer than a
  // microtask to background us, and the timeout is the backstop either way.
  void (async () => {
    try {
      const mod = await import("@capacitor/app" as any);
      const App = (mod as any).App;
      if (!App || typeof App.addListener !== "function" || settled) return;
      const handle = await App.addListener(
        "appStateChange",
        (state: { isActive: boolean }) => {
          if (!state?.isActive) finish(true);
        },
      );
      if (settled) {
        void handle?.remove?.();
        return;
      }
      removeNative = () => void handle?.remove?.();
    } catch {
      /* web build, or the plugin is unavailable — the DOM events cover it. */
    }
  })();

  return { result, cancel: () => finish(false) };
}
